import API_CONFIG from '../config/api.config';

class ApiService {
  constructor() {
    this.baseURL = API_CONFIG.baseURL;
    this.apiKey = API_CONFIG.apiKey;
    this.username = API_CONFIG.username;
    this.password = API_CONFIG.password;
    this.timeout = API_CONFIG.timeout;
    this.cache = new Map();
    this.pendingRequests = new Map();
    this.CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  }

  getAuthHeaders() {
    const credentials = btoa(`${this.username}:${this.password}`);
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Basic ${credentials}`,
    };
    
    if (this.apiKey) {
      headers['X-API-Key'] = this.apiKey;
    }
    
    return headers;
  }

  _cacheKey(endpoint, body) {
    return `${endpoint}:${body ? JSON.stringify(body) : ''}`;
  }

  _getCached(key) {
    const entry = this.cache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > this.CACHE_TTL) {
      this.cache.delete(key);
      return null;
    }
    return entry.data;
  }

  _setCached(key, data) {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  clearCache() {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  async request(endpoint, options = {}, useCache = false) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = this.getAuthHeaders();

    // If the caller supplies their own Authorization header (e.g. Bearer token),
    // remove the default Basic auth so it does not take precedence.
    if (options.headers?.Authorization) {
      delete headers.Authorization;
    }

    const config = {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    };

    // Check cache for cacheable requests
    const bodyKey = options.body || '';
    const cacheKey = this._cacheKey(endpoint, bodyKey);
    if (useCache) {
      const cached = this._getCached(cacheKey);
      if (cached) return cached;
      // Deduplicate concurrent identical requests
      if (this.pendingRequests.has(cacheKey)) {
        return this.pendingRequests.get(cacheKey);
      }
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    const requestPromise = (async () => {
      try {
        const response = await fetch(url, {
          ...config,
          mode: 'cors',
          credentials: 'omit',
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (useCache) {
          this._setCached(cacheKey, data);
        }
        return data;
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      } finally {
        if (useCache) {
          this.pendingRequests.delete(cacheKey);
        }
      }
    })();

    if (useCache) {
      this.pendingRequests.set(cacheKey, requestPromise);
    }

    return requestPromise;
  }

  async get(endpoint, options = {}, useCache = false) {
    return this.request(endpoint, {
      ...options,
      method: 'GET',
    }, useCache);
  }

  async post(endpoint, body, options = {}, useCache = false) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body),
    }, useCache);
  }

  async put(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'DELETE',
    });
  }
}

export default new ApiService();
